/**
 * Billing Ops Panel — Admin Cockpit (iter-21)
 *
 * Self-service diagnostics for the Stripe checkout/webhook loop. Surfaces:
 *   1. The exact webhook URL operators paste into Stripe Dashboard
 *   2. Env-var presence (booleans only — never values; SECURITY)
 *   3. Live webhook hit log so operators can SEE whether Stripe is reaching us
 *   4. One-click manual grant (Option C escape hatch)
 *
 * Backed by:
 *   GET  /api/admin/billing/diagnostics
 *   GET  /api/admin/billing/webhook-hits?limit=…
 *   POST /api/admin/billing/grant
 *
 * Lives outside Admin.jsx because Admin.jsx is already 2000+ lines and the
 * iter-19 refactor budget is locked.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ArrowsClockwise,
  CheckCircle,
  Copy,
  CreditCard,
  Lightning,
  Sparkle,
  WarningCircle,
  XCircle,
} from "@phosphor-icons/react";
import { Admin } from "../../lib/api";

const COPY_LABEL = "COPY";

function fmtTs(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toISOString().replace("T", " ").slice(0, 19) + "Z";
  } catch {
    return ts;
  }
}

function StatusDot({ ok, warn }) {
  const color = ok ? "#00AA5A" : warn ? "#FFB000" : "#FF2A2A";
  return (
    <span
      className="inline-block w-3 h-3 border-2 border-black shrink-0"
      style={{ background: color }}
      data-testid="billing-status-dot"
    />
  );
}

function EnvRow({ name, present, source, hint }) {
  const isOverride = source === "override";
  return (
    <div
      className="flex items-center justify-between gap-2 py-2 border-b border-neutral-200 last:border-b-0"
      data-testid={`billing-env-row-${name}`}
    >
      <span className="font-mono text-xs">{name}</span>
      <span className="flex items-center gap-2">
        {hint && <span className="font-mono text-[10px] text-neutral-500">{hint}</span>}
        {isOverride && (
          <span
            className="chip bg-amber-100 text-amber-900 border-amber-500"
            data-testid={`billing-env-source-${name}`}
            title="Value comes from the Mongo runtime-override, not the env var"
          >
            OVERRIDE
          </span>
        )}
        <span
          className={`chip ${present ? "chip-blue" : "chip-live"}`}
          data-testid={`billing-env-status-${name}`}
        >
          {present ? "SET" : "MISSING"}
        </span>
      </span>
    </div>
  );
}

function HitRow({ hit }) {
  const ok = hit.signature_valid && hit.post_processed;
  const warn = hit.signature_valid && !hit.post_processed && !hit.error;
  return (
    <tr
      className="border-b border-neutral-200 align-top"
      data-testid={`billing-hit-row-${hit.id}`}
    >
      <td className="py-2 pr-2 whitespace-nowrap">
        <StatusDot ok={ok} warn={warn} />
      </td>
      <td className="py-2 pr-2 font-mono text-[11px] whitespace-nowrap">
        {fmtTs(hit.ts)}
      </td>
      <td className="py-2 pr-2 font-mono text-[11px]">{hit.event_type || "—"}</td>
      <td className="py-2 pr-2 font-mono text-[11px] truncate max-w-[180px]" title={hit.email_extracted || ""}>
        {hit.email_extracted || <span className="text-neutral-400">none</span>}
      </td>
      <td className="py-2 pr-2 font-mono text-[10px]">
        {hit.source_hint || "—"}
      </td>
      <td className="py-2 pr-2">
        {!hit.signature_valid ? (
          <span className="chip chip-live">SIG INVALID</span>
        ) : hit.post_processed ? (
          <span className="chip chip-blue">PROCESSED</span>
        ) : hit.error ? (
          <span className="chip" title={hit.error}>
            {hit.error.slice(0, 24)}
          </span>
        ) : (
          <span className="chip">SEEN</span>
        )}
      </td>
    </tr>
  );
}

// ============ DeployReadinessCard (iter-29) ============
// Pre-cutover diagnostic. 4 checks: DNS · CORS · Resend · Redirect flag.
// The first 3 are backend-driven; the redirect flag lives in the frontend
// bundle (REACT_APP_REDIRECT_TO_PROD) so we read it from process.env here.
function DeployReadinessCard({ readiness, checking, onRefresh }) {
  // Frontend-only check — bundled at build time
  const redirectEnabled = (process.env.REACT_APP_REDIRECT_TO_PROD || "")
    .toString().toLowerCase() === "true";

  // Combine backend checks with the frontend-only redirect check
  const checks = readiness?.checks ? { ...readiness.checks } : {};
  if (checks.redirect_hint) {
    checks.redirect = {
      ok: redirectEnabled,
      detail: redirectEnabled
        ? "REACT_APP_REDIRECT_TO_PROD=true (legacy host bounces to prod)"
        : "REACT_APP_REDIRECT_TO_PROD unset/false (preview stays on emergent host)",
      severity: redirectEnabled ? "info" : "warn",
    };
    delete checks.redirect_hint;
  }
  // Compute overall from the combined checks
  const severities = Object.values(checks).map(c => c?.severity || "info");
  const overall = severities.includes("fail") ? "blocked"
    : severities.includes("warn") ? "partial"
    : (readiness ? "ready" : "unknown");

  const pillColor = overall === "ready" ? "#00AA5A"
    : overall === "partial" ? "#FFB000"
    : overall === "blocked" ? "#FF2A2A"
    : "#9E9E9E";
  const pillLabel = overall === "ready" ? "READY"
    : overall === "partial" ? "PARTIAL"
    : overall === "blocked" ? "BLOCKED"
    : "CHECKING…";

  const rowFor = (key, label) => {
    const c = checks[key];
    if (!c) return null;
    const dot = c.severity === "fail" ? "#FF2A2A"
      : c.severity === "warn" ? "#FFB000"
      : "#00AA5A";
    return (
      <div
        key={key}
        className="flex items-start gap-2 py-1.5 border-b border-neutral-200 last:border-b-0"
        data-testid={`readiness-row-${key}`}
      >
        <span
          className="inline-block w-2.5 h-2.5 border-2 border-black mt-1 shrink-0"
          style={{ background: dot }}
        />
        <div className="min-w-0 flex-1">
          <p className="label-mono text-[10px]">{label}</p>
          <p className="font-mono text-[11px] text-neutral-700 break-words">
            {c.detail}
          </p>
        </div>
      </div>
    );
  };

  return (
    <section
      className="hairline-2 p-4"
      style={{
        boxShadow: `4px 4px 0 0 ${pillColor}`,
        background: overall === "ready" ? "rgba(0,170,90,0.04)"
          : overall === "partial" ? "rgba(255,176,0,0.04)"
          : overall === "blocked" ? "rgba(255,42,42,0.04)"
          : "#FFFFFF",
      }}
      data-testid="billing-section-readiness"
    >
      <div className="flex items-baseline justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-baseline gap-2">
          <Sparkle size={16} weight="bold" />
          <h3 className="font-display font-bold tracking-tight">
            DEPLOY READINESS
          </h3>
          <span className="label-mono text-neutral-500">
            {readiness?.expected_domain || "aiimagesuites.pro"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="chip"
            style={{
              background: pillColor,
              color: overall === "partial" ? "#0A0A0A" : "#FFFFFF",
              borderColor: pillColor,
            }}
            data-testid="readiness-overall-pill"
          >
            {pillLabel}
          </span>
          <button
            onClick={onRefresh}
            disabled={checking}
            className="btn-flat-outline shrink-0 disabled:opacity-50"
            data-testid="readiness-refresh-btn"
            aria-label="Re-run readiness check"
          >
            <ArrowsClockwise size={14} weight="bold" />
            {checking ? "CHECKING…" : "RE-CHECK"}
          </button>
        </div>
      </div>
      <p className="font-mono text-xs text-neutral-600 mb-3">
        Verifies DNS · CORS · Resend · Redirect flag are all wired before you
        flip the production switch. Read-only; safe to run anytime.
      </p>
      <div className="bg-white border-2 border-black px-3 py-1">
        {rowFor("dns", "DNS")}
        {rowFor("cors", "CORS")}
        {rowFor("resend", "RESEND")}
        {rowFor("voice", "VOICE (WHISPER)")}
        {rowFor("redirect", "REDIRECT FLAG")}
      </div>
      {readiness?.checked_at && (
        <p className="label-mono text-neutral-400 text-[10px] mt-2">
          LAST CHECKED · {fmtTs(readiness.checked_at)}
        </p>
      )}
    </section>
  );
}

export default function BillingOpsPanel({ onAuthFail }) {
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [grantEmail, setGrantEmail] = useState("");
  const [grantNote, setGrantNote] = useState("");
  const [granting, setGranting] = useState(false);
  // iter-22: runtime secret override form
  const [overrideName, setOverrideName] = useState("STRIPE_WEBHOOK_SECRET");
  const [overrideValue, setOverrideValue] = useState("");
  const [savingOverride, setSavingOverride] = useState(false);
  // iter-29: deploy readiness pill
  const [readiness, setReadiness] = useState(null);
  const [checkingReadiness, setCheckingReadiness] = useState(false);

  const loadReadiness = async () => {
    setCheckingReadiness(true);
    try {
      const r = await Admin.Billing.deployReadiness();
      setReadiness(r);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        onAuthFail?.();
        return;
      }
      toast.error("Deploy readiness check failed");
    } finally {
      setCheckingReadiness(false);
    }
  };

  const load = async () => {
    setRefreshing(true);
    try {
      const d = await Admin.Billing.diagnostics();
      setDiag(d);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        onAuthFail?.();
        return;
      }
      toast.error("Failed to load billing diagnostics");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    loadReadiness();
    // Light auto-refresh every 15s so newly-arriving webhooks appear quickly
    // while the operator is staring at the panel during a live test.
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed");
    }
  };

  const handleGrant = async () => {
    const e = (grantEmail || "").trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) {
      toast.error("ENTER A VALID EMAIL");
      return;
    }
    if (!window.confirm(`Manually flip ${e} to sync_enabled=true? This is logged as an audit row.`)) return;
    setGranting(true);
    try {
      const r = await Admin.Billing.grant(e, grantNote.trim());
      toast.success(`GRANTED · ${r.email} · sync_enabled=${r.sync_enabled}`);
      setGrantEmail("");
      setGrantNote("");
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "GRANT FAILED";
      toast.error(String(msg).toUpperCase().slice(0, 120));
    } finally {
      setGranting(false);
    }
  };

  const handleSetOverride = async () => {
    const v = (overrideValue || "").trim();
    if (!v) {
      toast.error("VALUE CANNOT BE EMPTY");
      return;
    }
    if (!window.confirm(
      `Set runtime override for ${overrideName}? This stores the value in Mongo and takes precedence over the env var.`
    )) return;
    setSavingOverride(true);
    try {
      await Admin.Billing.setRuntimeSecret(overrideName, v);
      toast.success(`OVERRIDE SET · ${overrideName}`);
      setOverrideValue("");
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "OVERRIDE FAILED";
      toast.error(String(msg).toUpperCase().slice(0, 120));
    } finally {
      setSavingOverride(false);
    }
  };

  const handleClearOverride = async (name) => {
    if (!window.confirm(`Clear runtime override for ${name}? Future lookups fall back to the env var.`)) return;
    try {
      await Admin.Billing.clearRuntimeSecret(name);
      toast.success(`OVERRIDE CLEARED · ${name}`);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "CLEAR FAILED";
      toast.error(String(msg).toUpperCase().slice(0, 120));
    }
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="admin-billing-panel">
        <p className="label-mono text-neutral-500">LOADING BILLING DIAGNOSTICS…</p>
      </div>
    );
  }
  if (!diag) return null;

  const counts = diag.counts || {};
  const env = diag.env_present || {};
  const safe = diag.env_values_safe || {};
  const allEnvSet = Object.values(env).every(Boolean);

  return (
    <div className="space-y-6" data-testid="admin-billing-panel">
      {/* Header summary */}
      <div className="flex items-baseline justify-between">
        <h2 className="font-display font-black tracking-tighter text-2xl">
          BILLING OPS
        </h2>
        <button
          className="btn-flat-outline text-xs"
          onClick={load}
          disabled={refreshing}
          data-testid="billing-refresh-btn"
        >
          <ArrowsClockwise
            size={14}
            weight="bold"
            className={refreshing ? "animate-spin" : ""}
          />
          {refreshing ? "REFRESHING" : "REFRESH"}
        </button>
      </div>

      {/* Section 0 — Deploy readiness pill (iter-29) */}
      <DeployReadinessCard
        readiness={readiness}
        checking={checkingReadiness}
        onRefresh={loadReadiness}
      />

      {/* Section 1 — Webhook URL to paste in Stripe */}
      <section
        className="hairline-2 p-4 bg-neutral-50"
        data-testid="billing-section-webhook-url"
      >
        <div className="flex items-baseline gap-2 mb-2">
          <Lightning size={16} weight="bold" />
          <h3 className="font-display font-bold tracking-tight">
            STRIPE WEBHOOK URL
          </h3>
        </div>
        <p className="font-mono text-xs text-neutral-600 mb-3">
          Paste this exact URL into Stripe Dashboard → Developers → Webhooks →
          Add endpoint. Path matches the handler in <code>billing.py</code>.
        </p>
        <div className="flex items-center gap-2 bg-white border-2 border-black p-2">
          <code
            className="flex-1 font-mono text-xs break-all"
            data-testid="billing-webhook-url"
          >
            {diag.suggested_webhook_url}
          </code>
          <button
            className="btn-flat-outline shrink-0"
            onClick={() => copy(diag.suggested_webhook_url, "WEBHOOK URL")}
            data-testid="billing-copy-webhook-url"
            aria-label="Copy webhook URL"
          >
            <Copy size={14} weight="bold" /> {COPY_LABEL}
          </button>
        </div>
        <p className="label-mono text-neutral-500 text-[10px] mt-2">
          REQUIRED EVENTS: checkout.session.completed · checkout.session.expired
          · payment_intent.succeeded · payment_intent.payment_failed
        </p>
      </section>

      {/* Section 2 — Env-var presence checklist */}
      <section
        className="hairline-2 p-4 bg-white"
        data-testid="billing-section-env"
      >
        <div className="flex items-baseline justify-between mb-3">
          <div className="flex items-baseline gap-2">
            <CreditCard size={16} weight="bold" />
            <h3 className="font-display font-bold tracking-tight">
              ENVIRONMENT VARIABLES
            </h3>
          </div>
          <span
            className={`chip ${allEnvSet ? "chip-blue" : "chip-live"}`}
            data-testid="billing-env-overall-status"
          >
            {allEnvSet ? "ALL SET" : "INCOMPLETE"}
          </span>
        </div>
        <p className="font-mono text-xs text-neutral-600 mb-3">
          Presence-only readout. Values are never returned by the API.
        </p>
        <div className="border-2 border-black px-3 bg-neutral-50">
          <EnvRow name="STRIPE_API_KEY" present={env.STRIPE_API_KEY} source={(diag.env_source || {}).STRIPE_API_KEY} />
          <EnvRow
            name="STRIPE_WEBHOOK_SECRET"
            present={env.STRIPE_WEBHOOK_SECRET}
            source={(diag.env_source || {}).STRIPE_WEBHOOK_SECRET}
          />
          <EnvRow
            name="STRIPE_PRICE_ID_STARTER"
            present={env.STRIPE_PRICE_ID_STARTER}
            source={(diag.env_source || {}).STRIPE_PRICE_ID_STARTER}
            hint={safe.STRIPE_PRICE_ID_STARTER_SUFFIX}
          />
          <EnvRow
            name="SYNC_PAYWALL_ENABLED"
            present={env.SYNC_PAYWALL_ENABLED}
            source={(diag.env_source || {}).SYNC_PAYWALL_ENABLED}
            hint={safe.SYNC_PAYWALL_ENABLED ? "true" : "false"}
          />
          <EnvRow name="ADMIN_TOKEN" present={env.ADMIN_TOKEN} source={(diag.env_source || {}).ADMIN_TOKEN} />
          {/* iter-26 — welcome email (Resend) */}
          <EnvRow
            name="RESEND_API_KEY"
            present={env.RESEND_API_KEY}
            source={(diag.env_source || {}).RESEND_API_KEY}
          />
          <EnvRow
            name="RESEND_FROM_EMAIL"
            present={env.RESEND_FROM_EMAIL}
            source={(diag.env_source || {}).RESEND_FROM_EMAIL}
            hint={env.RESEND_FROM_EMAIL ? null : "defaults to onboarding@resend.dev"}
          />
        </div>
      </section>

      {/* Section 3 — Counts */}
      <section
        className="grid grid-cols-2 md:grid-cols-4 gap-3"
        data-testid="billing-section-counts"
      >
        {[
          ["PAID USERS", counts.paid_users, "billing-count-paid"],
          ["TOTAL USERS", counts.total_users, "billing-count-users"],
          ["TXN ROWS", counts.total_txns, "billing-count-txns"],
          ["WEBHOOK HITS", counts.total_webhook_hits, "billing-count-hits"],
        ].map(([label, value, tid]) => (
          <div
            key={label}
            className="hairline-2 p-3 bg-white"
            data-testid={tid}
          >
            <p className="label-mono text-neutral-500">{label}</p>
            <p className="font-display font-black text-3xl tracking-tighter">
              {value ?? 0}
            </p>
          </div>
        ))}
      </section>

      {/* Section 4 — Recent webhook hits */}
      <section
        className="hairline-2 p-4 bg-white"
        data-testid="billing-section-hits"
      >
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="font-display font-bold tracking-tight">
            RECENT WEBHOOK HITS
          </h3>
          <span className="label-mono text-neutral-500">
            NEWEST FIRST · AUTO-REFRESH 15s
          </span>
        </div>
        {!diag.recent_webhook_hits?.length ? (
          <div
            className="border-2 border-dashed border-neutral-400 p-6 text-center"
            data-testid="billing-hits-empty"
          >
            <WarningCircle size={20} className="inline mr-1" weight="bold" />
            <span className="label-mono text-neutral-600">
              NO WEBHOOK HITS RECORDED YET
            </span>
            <p className="font-mono text-xs text-neutral-500 mt-2">
              Either Stripe isn&apos;t configured to send events here yet, or it
              hasn&apos;t fired one. Configure the URL above in your Stripe
              Dashboard and pay $0.50 with the Stripe CLI test card to confirm
              the loop.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="billing-hits-table">
              <thead>
                <tr className="border-b-2 border-black">
                  <th className="py-2 pr-2 label-mono text-[10px]">OK</th>
                  <th className="py-2 pr-2 label-mono text-[10px]">TS</th>
                  <th className="py-2 pr-2 label-mono text-[10px]">EVENT</th>
                  <th className="py-2 pr-2 label-mono text-[10px]">EMAIL</th>
                  <th className="py-2 pr-2 label-mono text-[10px]">SOURCE</th>
                  <th className="py-2 pr-2 label-mono text-[10px]">STATUS</th>
                </tr>
              </thead>
              <tbody>
                {diag.recent_webhook_hits.map((h) => (
                  <HitRow key={h.id} hit={h} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Section 5 — Recent paid users */}
      <section
        className="hairline-2 p-4 bg-white"
        data-testid="billing-section-paid-users"
      >
        <h3 className="font-display font-bold tracking-tight mb-3">
          RECENT ACTIVE SUBSCRIBERS
        </h3>
        {!diag.recent_paid_users?.length ? (
          <p className="font-mono text-xs text-neutral-500">
            No paid users yet.
          </p>
        ) : (
          <ul className="divide-y-2 divide-black border-2 border-black">
            {diag.recent_paid_users.map((u) => (
              <li
                key={u.email}
                className="p-3 flex items-center justify-between gap-3"
                data-testid={`billing-paid-row-${u.email}`}
              >
                <div className="min-w-0">
                  <p className="font-mono text-xs truncate">{u.email}</p>
                  <p className="label-mono text-neutral-500 text-[10px]">
                    {(u.subscription_status || "—").toUpperCase()} ·{" "}
                    {u.plan || "—"} · {fmtTs(u.updated_at)}
                  </p>
                </div>
                <CheckCircle
                  size={20}
                  weight="fill"
                  className="text-emerald-600 shrink-0"
                />
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Section 5.5 — Runtime Secret Override (iter-22 / Option B) */}
      <section
        className="hairline-2 p-4 bg-white"
        style={{ boxShadow: "4px 4px 0 0 #FFB000" }}
        data-testid="billing-section-override"
      >
        <div className="flex items-baseline gap-2 mb-2">
          <Lightning size={16} weight="bold" />
          <h3 className="font-display font-bold tracking-tight">
            RUNTIME SECRET OVERRIDE
          </h3>
        </div>
        <p className="font-mono text-xs text-neutral-600 mb-3">
          Fallback for when the deploy platform blocks adding a new env var.
          Stores the value in Mongo and takes precedence over the env var of
          the same name. Supported: <code>STRIPE_WEBHOOK_SECRET</code>,{" "}
          <code>STRIPE_PRICE_ID_STARTER</code>, <code>RESEND_API_KEY</code>,{" "}
          <code>RESEND_FROM_EMAIL</code>. Env var is the preferred long-term path.
        </p>

        {/* Active overrides — surface so operator knows what's currently in effect */}
        {(diag.overrides_active || []).length > 0 && (
          <div className="mb-3 border-2 border-amber-500 bg-amber-50 p-2">
            <p className="label-mono text-amber-900 mb-1">ACTIVE OVERRIDES</p>
            <ul className="space-y-1">
              {diag.overrides_active.map((name) => (
                <li
                  key={name}
                  className="flex items-center justify-between gap-2"
                  data-testid={`billing-override-active-${name}`}
                >
                  <code className="font-mono text-[11px]">{name}</code>
                  <button
                    onClick={() => handleClearOverride(name)}
                    className="btn-flat-outline text-[10px] py-1 px-2"
                    data-testid={`billing-override-clear-${name}`}
                  >
                    CLEAR
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="space-y-2">
          <label className="block">
            <span className="label-mono block mb-1">SECRET</span>
            <select
              value={overrideName}
              onChange={(e) => setOverrideName(e.target.value)}
              className="w-full border-2 border-black px-3 py-2 font-mono text-sm bg-white"
              data-testid="billing-override-name-select"
            >
              <option value="STRIPE_WEBHOOK_SECRET">STRIPE_WEBHOOK_SECRET</option>
              <option value="STRIPE_PRICE_ID_STARTER">STRIPE_PRICE_ID_STARTER</option>
              <option value="RESEND_API_KEY">RESEND_API_KEY</option>
              <option value="RESEND_FROM_EMAIL">RESEND_FROM_EMAIL</option>
            </select>
          </label>
          <input
            type={overrideName === "RESEND_FROM_EMAIL" ? "text" : "password"}
            placeholder={
              overrideName === "STRIPE_WEBHOOK_SECRET" ? "whsec_..."
                : overrideName === "STRIPE_PRICE_ID_STARTER" ? "price_..."
                : overrideName === "RESEND_API_KEY" ? "re_..."
                : overrideName === "RESEND_FROM_EMAIL" ? "noreply@aiimagesuites.pro"
                : ""
            }
            value={overrideValue}
            onChange={(e) => setOverrideValue(e.target.value)}
            className="w-full border-2 border-black px-3 py-2 font-mono text-sm"
            data-testid="billing-override-value-input"
            autoComplete="off"
          />
          <button
            onClick={handleSetOverride}
            disabled={savingOverride || !overrideValue.trim()}
            className="btn-flat w-full py-3 inline-flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="billing-override-save-btn"
          >
            {savingOverride ? (
              <>
                <ArrowsClockwise
                  size={14}
                  weight="bold"
                  className="animate-spin"
                />
                SAVING…
              </>
            ) : (
              <>
                <Lightning size={14} weight="bold" /> SET OVERRIDE
              </>
            )}
          </button>
        </div>
        <p className="label-mono text-neutral-400 mt-3 text-[10px]">
          <XCircle size={10} weight="bold" className="inline mr-1" />
          VALUE NEVER RETURNED BY API · ALL CHANGES LOGGED
        </p>
      </section>

      {/* Section 6 — Manual grant (Option C escape hatch) */}
      <section
        className="hairline-2 p-4 bg-white"
        style={{ boxShadow: "4px 4px 0 0 #FF2A2A" }}
        data-testid="billing-section-grant"
      >
        <div className="flex items-baseline gap-2 mb-2">
          <Sparkle size={16} weight="bold" />
          <h3 className="font-display font-bold tracking-tight">
            MANUAL GRANT — RECONCILIATION ONLY
          </h3>
        </div>
        <p className="font-mono text-xs text-neutral-600 mb-3">
          Use ONLY when a real Stripe payment exists but the webhook missed
          the buyer (e.g. Buy Button purchase made before this endpoint was
          configured in Stripe). Writes an audit row tagged{" "}
          <code>source: admin_manual</code>.
        </p>
        <div className="space-y-2">
          <input
            type="email"
            placeholder="buyer@example.com"
            value={grantEmail}
            onChange={(e) => setGrantEmail(e.target.value)}
            className="w-full border-2 border-black px-3 py-2 font-mono text-sm"
            data-testid="billing-grant-email-input"
          />
          <input
            type="text"
            placeholder="audit note (why was this granted manually)"
            value={grantNote}
            onChange={(e) => setGrantNote(e.target.value)}
            maxLength={256}
            className="w-full border-2 border-black px-3 py-2 font-mono text-sm"
            data-testid="billing-grant-note-input"
          />
          <button
            onClick={handleGrant}
            disabled={granting || !grantEmail.trim()}
            className="btn-flat w-full py-3 inline-flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="billing-grant-submit-btn"
          >
            {granting ? (
              <>
                <ArrowsClockwise
                  size={14}
                  weight="bold"
                  className="animate-spin"
                />
                GRANTING…
              </>
            ) : (
              <>
                <Sparkle size={14} weight="bold" /> GRANT SYNC ACCESS
              </>
            )}
          </button>
        </div>
        <p className="label-mono text-neutral-400 mt-3 text-[10px]">
          <XCircle size={10} weight="bold" className="inline mr-1" />
          USE SPARINGLY · EVERY GRANT IS LOGGED
        </p>
      </section>
    </div>
  );
}
